"use client"

import { datadog } from "@/platform/observability/datadog/facade"

import { trackEvent } from "./track"

import type {
  AppDownloadClickEvent,
  CheckoutStepPageViewEvent,
  CheckoutButtonClickEvent,
  CheckoutCompleteEvent,
  CreditLimitExceedsRequestedEvent,
  LoanApplicationApprovedEvent,
  LoanApplicationRejectedEvent,
  SelectRepaymentOptionEvent,
  DownPaymentCompletedEvent,
  SubmitCheckoutEvent,
} from "./types/custom"
import type { ViewCartEvent, RemoveFromCartEvent, BeginCheckoutEvent, AddShippingInfoEvent, PurchaseEvent } from "./types/google"
import type { PdpEarlyTapEvent } from "./types/pdp"

export function trackViewCart(params: ViewCartEvent["params"]): void {
  trackEvent("view_cart", params)
}

export function trackRemoveFromCart(params: RemoveFromCartEvent["params"]): void {
  trackEvent("remove_from_cart", params)
}

export function trackBeginCheckout(params: BeginCheckoutEvent["params"]): void {
  trackEvent("begin_checkout", params)
}

export function trackAddShippingInfo(params: AddShippingInfoEvent["params"]): void {
  trackEvent("add_shipping_info", params)
}

export function trackPurchase(params: PurchaseEvent["params"] & { is_first_loan?: boolean }): void {
  trackEvent("purchase", params)
}

export function trackCheckoutStepView(params: CheckoutStepPageViewEvent["params"]): void {
  trackEvent("checkout_step_page_view", params)
  datadog.addAction(`checkout.${params.step}`, params)
}

/**
 * @deprecated Legacy event for Facebook Pixel integration via GTM.
 */
export function trackCheckoutButtonClick(params: CheckoutButtonClickEvent["params"]): void {
  trackEvent("checkout_button_click", params)
}

export function trackPdpEarlyTap(params: PdpEarlyTapEvent["params"]): void {
  trackEvent("pdp_early_tap", params)
}

export function trackLoanApplicationApproved(params: LoanApplicationApprovedEvent["params"]): void {
  trackEvent("loan_application_approved", params)
}

export function trackLoanApplicationRejected(params: LoanApplicationRejectedEvent["params"]): void {
  trackEvent("loan_application_rejected", params)
}

export function trackCreditLimitExceedsRequested(params: CreditLimitExceedsRequestedEvent["params"]): void {
  trackEvent("credit_limit_exceeds_requested", params)
}

export function trackRepaymentSelect(params: SelectRepaymentOptionEvent["params"]): void {
  trackEvent("select_repayment_option", params)
}

export function trackDownPaymentComplete(params: DownPaymentCompletedEvent["params"]): void {
  trackEvent("down_payment_completed", params)
}

export function trackCheckoutSubmit(params: SubmitCheckoutEvent["params"]): void {
  trackEvent("submit_checkout", params)
}

export function trackCheckoutComplete(params: CheckoutCompleteEvent["params"]): void {
  trackEvent("checkout_complete", params)
}

export function trackAppDownloadClick(params: AppDownloadClickEvent["params"]): void {
  trackEvent("app_download_click", params)
}

// RUM-only funnel markers for the logged-in-but-unverified BNPL path (ENG-20346).
export function trackBnplLoginRequiresIdv(): void {
  datadog.addAction("bnpl.login_requires_idv")
}

// A signed-in browser that reopened the checkout and skipped the phone and code screens.
export function trackBnplResume(params: { step: string; restored_plan: boolean }): void {
  datadog.addAction("bnpl.resume", params)
}

export function trackBnplConfirmIdentityBlocked(params: { identity_status: string; failed_attempts: number }): void {
  datadog.addAction("bnpl.confirm_identity_blocked", params)
}

export function trackBnplWelcomeDiscountUnavailable(params: { discount_percent: number }): void {
  datadog.addAction("bnpl.welcome_discount_unavailable", params)
}

export function trackCobrandCreditApprovedContinue(): void {
  datadog.addAction("cobrand.credit_approved.continue")
}

export function trackCobrandIdentityWaitTimeoutNotice(): void {
  datadog.addAction("cobrand.wait.identity_timeout_notice")
}
